import { useEffect, useState } from "react";
import { User, Lock, Bell, Crown, BadgeCheck, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

const NOTIF_LABELS = {
  new_reservation: ["Nouvelles réservations", "Reçois une alerte à chaque réservation sur tes événements."],
  new_review: ["Nouveaux avis", "Sois notifié quand un client laisse un avis."],
  admin_reply: ["Réponses de l'admin", "Sois notifié quand l'équipe GoSpot répond à tes messages."],
};

const Card = ({ icon: Icon, title, desc, testId, children }) => (
  <div data-testid={testId} className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
    <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
      <Icon className="h-4 w-4 text-[#2563EB]" />
      {title}
    </h2>
    {desc && <p className="mt-1 text-xs text-slate-500">{desc}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

export const SettingsView = ({ go }) => {
  const { t, lang } = useLanguage();
  const [account, setAccount] = useState(null);
  const [name, setName] = useState("");
  const [notifs, setNotifs] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(null);

  const load = () =>
    api.get("/pro/account").then((r) => {
      setAccount(r.data);
      setName(r.data.name);
      setNotifs(r.data.notifications);
    }).catch(() => setAccount(null));

  useEffect(() => {
    load();
  }, []);

  const saveProfile = async () => {
    setBusy("profile");
    try {
      await api.patch("/pro/account", { name });
      toast.success(t("Informations mises à jour"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const toggleNotif = async (key) => {
    const next = { ...notifs, [key]: !notifs[key] };
    setNotifs(next);
    try {
      await api.patch("/pro/account", { notifications: next });
      toast.success(t("Préférences enregistrées"));
    } catch (e) {
      setNotifs(notifs);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("Les deux mots de passe ne correspondent pas"));
      return;
    }
    setBusy("pwd");
    try {
      await api.post("/pro/account/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(t("Mot de passe modifié"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!account) {
    return <div className="mt-6 h-96 animate-pulse rounded-3xl bg-white" />;
  }

  return (
    <div className="mt-6 grid max-w-5xl gap-5 lg:grid-cols-2" data-testid="pro-settings-view">
      <Card icon={User} title={t("Informations du compte")} desc="Ces informations identifient ton compte propriétaire." testId="settings-account">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">{t("Nom complet")}</label>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">{t("Courriel")}</label>
            <input data-testid="settings-email" value={account.email} disabled className={`${inputClass} bg-slate-50 text-slate-400`} />
          </div>
          <div className="flex items-center justify-between rounded-2xl bg-[#F8FAFC] px-4 py-3">
            <span className="text-xs font-medium text-slate-500">{t("Rôle")}</span>
            <span className="flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-bold text-[#2563EB]" data-testid="settings-role">
              <BadgeCheck className="h-3.5 w-3.5" />
              {account.role === "pro" ? "Propriétaire Pro" : account.role === "admin" ? "Administrateur" : "Membre"}
            </span>
          </div>
          <p className="text-[11px] text-slate-400">Membre depuis {account.member_since}</p>
          <button type="button" data-testid="settings-save-profile" onClick={saveProfile} disabled={busy === "profile"} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-60">
            <Save className="h-3.5 w-3.5" />
            {busy === "profile" ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </Card>

      <Card icon={Lock} title={t("Mot de passe")} desc="Choisis un mot de passe d'au moins 6 caractères." testId="settings-password">
        <form onSubmit={changePassword} className="space-y-3">
          <input data-testid="settings-pwd-current" type="password" required value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} placeholder={t("Mot de passe actuel")} className={inputClass} />
          <input data-testid="settings-pwd-new" type="password" required minLength={6} value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} placeholder={t("Nouveau mot de passe")} className={inputClass} />
          <input data-testid="settings-pwd-confirm" type="password" required minLength={6} value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} placeholder={t("Confirme le nouveau mot de passe")} className={inputClass} />
          <button type="submit" data-testid="settings-pwd-submit" disabled={busy === "pwd"} className="flex items-center gap-1.5 rounded-full bg-[#0F172A] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:opacity-60">
            <KeyRound className="h-3.5 w-3.5" />
            {busy === "pwd" ? "Modification…" : "Modifier le mot de passe"}
          </button>
        </form>
      </Card>

      <Card icon={Bell} title={t("Notifications")} desc="Choisis les alertes que tu veux recevoir." testId="settings-notifications">
        <div className="space-y-2">
          {Object.entries(NOTIF_LABELS).map(([key, [label, desc]]) => (
            <label key={key} data-testid={`settings-notif-${key}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-100 px-4 py-3 transition hover:border-blue-200">
              <span>
                <span className="block text-sm font-semibold text-slate-800">{label}</span>
                <span className="block text-[11px] text-slate-400">{desc}</span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={!!notifs[key]}
                data-testid={`settings-notif-toggle-${key}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleNotif(key);
                }}
                className={`relative h-6 w-11 shrink-0 rounded-full transition ${notifs[key] ? "bg-[#2563EB]" : "bg-slate-200"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${notifs[key] ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>
          ))}
        </div>
      </Card>

      <Card icon={Crown} title={t("Forfait actuel")} desc="Ton abonnement GoSpot Pro." testId="settings-plan">
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-[#EFF6FF] to-white p-5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
              <Crown className="h-5 w-5 text-[#2563EB]" />
              Forfait {account.plan}
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-600">{t("Actif")}</span>
          </div>
          <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
            <li className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-[#2563EB]" /> {t("Fiche établissement complète et vérifiée")}</li>
            <li className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-[#2563EB]" /> {t("Événements et promotions illimités")}</li>
            <li className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-[#2563EB]" /> {t("Statistiques et support prioritaire")}</li>
          </ul>
          <p className="mt-4 text-[11px] text-slate-400">
            {t("La gestion de l'abonnement (changement de forfait, facturation) passe par l'équipe GoSpot.")}
          </p>
          <button
            type="button"
            data-testid="settings-contact-plan"
            onClick={() => go("contact")}
            className="mt-3 flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB]"
          >
            {t("Gérer mon abonnement avec l'admin")}
          </button>
        </div>
      </Card>
    </div>
  );
};
